import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Search } from 'lucide-react';

interface Column<T> {
  header: string;
  accessorKey?: keyof T;
  cell?: (item: T) => ReactNode;
  className?: string;
}

interface AdminTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string | number;
  searchPlaceholder?: string;
  onSearch?: (query: string) => void;
  actions?: ReactNode;
  emptyState?: ReactNode;
}

export function AdminTable<T>({
  data,
  columns,
  keyExtractor,
  searchPlaceholder,
  onSearch,
  actions,
  emptyState
}: AdminTableProps<T>) {
  return (
    <div className="bg-card border border-border rounded-xl shadow-sm flex flex-col overflow-hidden">
      {/* Header Bar */}
      {(onSearch || actions) && (
        <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-4 justify-between items-center">
          {onSearch && (
            <div className="relative w-full sm:max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder={searchPlaceholder || 'Search...'}
                onChange={(e) => onSearch(e.target.value)}
                className="w-full bg-background border border-border rounded-lg pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary transition-shadow"
              />
            </div>
          )}
          {actions && <div className="flex items-center gap-2 w-full sm:w-auto">{actions}</div>}
        </div>
      )}

      {/* Table Container */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left whitespace-nowrap">
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th key={i} className={cn("px-4 py-3", col.className)}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-muted-foreground">
                  {emptyState || 'No records found.'}
                </td>
              </tr>
            ) : (
              data.map((item) => (
                <tr 
                  key={keyExtractor(item)}
                  className="hover:bg-muted/50 transition-colors"
                >
                  {columns.map((col, i) => (
                    <td key={i} className={cn("px-4 py-3 align-middle", col.className)}>
                      {col.cell 
                        ? col.cell(item) 
                        : col.accessorKey 
                          ? String(item[col.accessorKey] ?? '') 
                          : null}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
